import React, { useContext, useEffect, useRef, useState } from "react";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  deleteDoc,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import "./CreateCircleInfo.scss";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { MemberContext } from "../Contexts/MemberContext";

const CreateCircleInfo = ({ setPostShow }) => {
  const { circleId, eventState } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const textareaRef = useRef(null);

  const { circleInfoList } = location.state || {};

  const [image, setImage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(1);
  const [selectedDay, setSelectedDay] = useState(1);
  const [maxDays, setMaxdays] = useState(null);
  const [daySelectShow, setDaySelectShow] = useState(true);

  const [circleInfo, setCircleInfo] = useState({
    title: "",
    eventday: "",
    selectedYear: "2025",
    selectedMonth: "1",
    selectedDay: "1",
    content: "",
    linkUrl: "",
    created_at: "",
    updated_at: "",
  });

  /** ✅ MemberContext（1回だけ） */
  const { currentUser, currentUserInfo, membersLoading } =
    useContext(MemberContext);

  // -------------------------------------------------
  // 🔐 管理者のみアクセス可
  // -------------------------------------------------
  useEffect(() => {
    if (membersLoading) return;

    if (!currentUser) {
      alert("ログインしてください。");
      navigate("/login");
      return;
    }

    if (currentUserInfo?.administrator !== true) {
      alert("投稿は管理者のみ可能です。");
      navigate("/eventlist");
    }
  }, [membersLoading, currentUser, currentUserInfo, navigate]);

  // -------------------------------------------------
  // 編集モード初期化
  // -------------------------------------------------
  useEffect(() => {
    if (circleId && Array.isArray(circleInfoList)) {
      const circleDoc = circleInfoList.find((c) => c.id === circleId);
      if (circleDoc) {
        setCircleInfo({ ...circleDoc });
        setIsEditing(true);
      }
    }
  }, [circleId, circleInfoList]);

  // -------------------------------------------------
  // textarea 自動リサイズ
  // -------------------------------------------------
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "20px";
      textareaRef.current.style.height =
        textareaRef.current.scrollHeight + "px";
    }
  }, [circleInfo.content, circleInfo.linkUrl]);

  // -------------------------------------------------
  // 日付最大日数計算
  // -------------------------------------------------
  useEffect(() => {
    const y = Number(circleInfo.selectedYear);
    const m = Number(circleInfo.selectedMonth);

    if ([1, 3, 5, 7, 8, 10, 12].includes(m)) setMaxdays(31);
    else if ([4, 6, 9, 11].includes(m)) setMaxdays(30);
    else if (m === 2) {
      const leap = y % 400 === 0 || (y % 4 === 0 && y % 100 !== 0);
      setMaxdays(leap ? 29 : 28);
    }
  }, [circleInfo.selectedYear, circleInfo.selectedMonth]);

  // -------------------------------------------------
  // セレクト生成
  // -------------------------------------------------
  const years = [];
  for (let y = 1850; y <= 2100; y++) years.push(y);

  const months = [];
  for (let m = 1; m <= 12; m++) months.push(m);

  const days = [];
  for (let d = 1; d <= (maxDays ?? 31); d++) days.push(d);

  // -------------------------------------------------
  // 入力変更
  // -------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCircleInfo((prev) => ({ ...prev, [name]: value }));
  };

  // -------------------------------------------------
  // 画像処理（元構造維持）
  // -------------------------------------------------
  const handleImageChange = (e) => {
    if (!e.target.files[0]) return;

    const reader = new FileReader();
    reader.readAsDataURL(e.target.files[0]);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        const minSize = Math.min(img.width, img.height);
        const offsetX = (img.width - minSize) / 2;
        const offsetY = (img.height - minSize) / 2;

        canvas.width = 800;
        canvas.height = 800;
        ctx.drawImage(img, offsetX, offsetY, minSize, minSize, 0, 0, 800, 800);

        canvas.toBlob((blob) => setImage(blob), "image/jpeg", 0.7);
      };
    };
  };

  // -------------------------------------------------
  // 投稿・更新
  // -------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    let imageUrl = circleInfo.imageUrl ?? "";

    if (image) {
      const storageRef = ref(storage, `blog-images/${Date.now()}.jpg`);
      const uploadTask = uploadBytesResumable(storageRef, image);

      await new Promise((resolve, reject) => {
        uploadTask.on("state_changed", null, reject, async () => {
          imageUrl = await getDownloadURL(uploadTask.snapshot.ref);
          resolve();
        });
      });
    }

    const eventdate = new Date(
      circleInfo.selectedYear,
      circleInfo.selectedMonth - 1,
      circleInfo.selectedDay
    );

    const eventdatestring = `${circleInfo.selectedYear}年${circleInfo.selectedMonth}月${circleInfo.selectedDay}日`;

    if (!isEditing) {
      await addDoc(collection(db, "circleinfo"), {
        ...circleInfo,
        imageUrl,
        eventdate,
        eventdatestring,
        created_at: serverTimestamp(),
      });
      setPostShow(false);
      navigate("/circleinfo");
    } else {
      await updateDoc(doc(db, "circleinfo", circleId), {
        ...circleInfo,
        imageUrl,
        eventdate,
        eventdatestring,
        updated_at: serverTimestamp(),
      });
      navigate("/circleinfo", { state: { pagestate: "create" } });
    }

    setLoading(false);
  };

  // -------------------------------------------------
  // 削除
  // -------------------------------------------------
  const handleDelete = async () => {
    const ok = window.confirm(`本当に${circleInfo.title}を削除しますか？`);
    if (!ok) return;

    await deleteDoc(doc(db, "circleinfo", circleId));
    navigate("/circleinfo");
  };

  // -------------------------------------------------
  // ローディング
  // -------------------------------------------------
  if (membersLoading) {
    return (
      <div className="createcircleinfo_container">
        <div className="createcircleinfo_content">読み込み中...</div>
      </div>
    );
  }

  return (
    <>
      <div className="createcircleinfo_container">
        <div className="createcircleinfo_content">
          <h2>
            <span className="createcircleinfo_content_title">
              {isEditing ? "出来事を編集" : "出来事を追加"}
            </span>
            <div
              className="createpost-close-button"
              onClick={
                isEditing
                  ? () => navigate("/circleinfo")
                  : () => setPostShow(false)
              }
            >
              ｘ
            </div>
          </h2>

          <form onSubmit={handleSubmit}>
            <div className="createcircleinfo_field">
              {!isEditing && (
                <>
                  <h3>日付</h3>
                  <span className="createcircleinfo_field_comment">
                    この日付は表示と並び替えに使用されます。
                  </span>
                  <div className="createcircleinfo_field_inner">
                    <select
                      id="createcircleinfo_field_inner-select"
                      value={circleInfo.selectedYear}
                      name="selectedYear"
                      onChange={handleChange}
                    >
                      {years.map((y) => (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      ))}
                    </select>
                    <p>年</p>

                    <select
                      id="createcircleinfo_field_inner-select"
                      value={circleInfo.selectedMonth}
                      name="selectedMonth"
                      onChange={handleChange}
                    >
                      {months.map((m) => (
                        <option key={m} value={m}>
                          {m}
                        </option>
                      ))}
                    </select>
                    <p>月</p>

                    {daySelectShow && (
                      <>
                        <select
                          id="createcircleinfo_field_inner-select"
                          value={circleInfo.selectedDay}
                          name="selectedDay"
                          onChange={handleChange}
                        >
                          {days.map((d) => (
                            <option key={d} value={d}>
                              {d}
                            </option>
                          ))}
                        </select>
                        <p>日</p>
                      </>
                    )}
                  </div>
                </>
              )}

              {isEditing && (
                <>
                  <h3>月日</h3>
                  <div className="createcircleinfo_field_inner">
                    <input
                      type="text"
                      name="eventdatestring"
                      value={circleInfo.eventdatestring}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              <h3>タイトル</h3>
              <div className="createcircleinfo_field_inner">
                <input
                  type="text"
                  name="title"
                  value={circleInfo.title}
                  onChange={handleChange}
                  required
                />
              </div>

              <h3>内 容</h3>
              <div className="createcircleinfo_field_inner2">
                <textarea
                  ref={textareaRef}
                  name="content"
                  value={circleInfo.content}
                  onChange={handleChange}
                  required
                />
              </div>

              <h3>リンク情報</h3>
              <div className="createcircleinfo_field_inner2">
                <textarea
                  ref={textareaRef}
                  name="linkUrl"
                  value={circleInfo.linkUrl}
                  onChange={handleChange}
                />
              </div>

              <h3>アップする写真</h3>
              <div className="createcircleinfo_field_inner">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="createcircleinfo_submit_button"
              >
                {isEditing ? "編集を実行" : loading ? "投稿中..." : "投稿"}
              </button>

              {isEditing && (
                <button
                  type="button"
                  className="createcircleinfo_submit_button"
                  style={{ color: "red" }}
                  onClick={handleDelete}
                >
                  削除する
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateCircleInfo;
